/**
 * MONSTER TOWN — PERFORMANCE OWNERSHIP (src/perf/**).
 *
 * THE GATE IS AN OLDER IPAD. Everything in this folder exists to keep a town a
 * kid has actually filled — hundreds of placed objects, a furnished house, a
 * furnished venue, neighbours walking — inside the frame budget of a
 * hand-me-down.
 *
 * Three things live here:
 *   1. `window.__perf` — scene-composition diagnostics. Draw calls are the whole
 *      ballgame in this game, so a tool can ask exactly where they come from.
 *   2. The MAXED-TOWN stress fixture (`stress.ts`). A kid WILL fill the plot;
 *      the fixture builds that state deterministically so the budget is measured
 *      against the worst case, not the demo.
 *   3. The GPU-pressure governor (`governor.ts`). The renderer's adaptive scale
 *      only ever watched CPU work, which on a GPU-bound older iPad never moves.
 *
 * This module is additive: `installPerf()` is one line in main.ts and nothing in
 * the frozen core changes.
 */

import * as THREE from 'three'
import type { GameContext } from '../app/context'
import { engine } from '../core/engine'
import { save } from '../core/save'
import { world } from '../core/scene'
import {
  batcherReport,
  batcherTick,
  installBatcher,
  setBatchingEnabled,
  type BatcherReport,
} from './batcher'
import { bakeVeto, liveNamesFor } from './bakeable'
import { instanceState } from './batcher'
import { installGovernor, governorReport, type GovernorReport } from './governor'
import { profileSystems, type SystemTiming } from './profile'
import { buildStress, type StressOptions, type StressReport } from './stress'

export interface LayerCount {
  meshes: number
  /** Meshes whose `visible` chain is true — i.e. actually submitted. */
  visible: number
  triangles: number
  /** Distinct materials referenced. Each is at least one draw call. */
  materials: number
  /** Instanced meshes seen, and the instances they carry. */
  instanced: number
  instances: number
}

export interface SceneReport {
  id: string
  awake: boolean
  attached: boolean
  placements: number
  ground: LayerCount
  statics: LayerCount
  placeables: LayerCount
  actors: LayerCount
}

export interface DefCost {
  defId: string
  count: number
  /** Meshes in one instance the batcher may merge. */
  bakeableEach: number
  /** Why the rest were left out, worst reason first. */
  vetoes: string[]
  /** Meshes in ONE instance's art group. */
  meshesEach: number
  /** Distinct materials in one instance — the floor on its draw calls. */
  materialsEach: number
  trianglesEach: number
  /** count * meshesEach — what this def costs the frame today. */
  meshesTotal: number
}

export interface PerfReport {
  drawCalls: number
  triangles: number
  programs: number
  renderScale: number
  pixelRatio: number
  workMs: number
  /** CPU work with the GL submit removed — the portable number. */
  simP50: number
  simP95: number
  /** Time inside `renderer.render()`. Reported, never gated. See the note. */
  glP50: number
  glP95: number
  /** Meshes reachable from the renderer's scene root, regardless of layer. */
  sceneMeshes: number
  visibleMeshes: number
  scenes: SceneReport[]
  defs: DefCost[]
  governor: GovernorReport
  batcher: BatcherReport
}

export interface PerfApi {
  report(): PerfReport
  /** Build the maxed-out town. Returns what it actually placed. */
  stress(opts?: StressOptions): Promise<StressReport>
  /** Governor knobs so a perf run can pin behaviour. */
  setGovernor(on: boolean): void
  governor(): GovernorReport
  /** A/B the batcher — the before/after captures compare these two states. */
  setBatching(on: boolean): void
  /** Batch bookkeeping for one placed instance, for the tap regression test. */
  instanceState(instanceId: string): {
    baked: boolean
    suspended: boolean
    neverBatch: boolean
    hiddenMeshes: number
    visibleHidden: number
  }
  batcher(): BatcherReport
  /** Rank the registered systems by frame cost over a measured window. */
  profile(ms?: number): Promise<SystemTiming[]>
  /**
   * Stop everything that moves so two captures can be compared pixel for pixel.
   * Without it an A/B of the batcher measures the neighbours walking.
   */
  freeze(on: boolean): void
  /** Split one frame into matrix walk / shadow pass / main pass. */
  frameBreakdown(samples?: number): Record<string, number>
  /** Hide a layer of the active scene to attribute cost. Diagnostics only. */
  setLayerVisible(layer: 'ground' | 'statics' | 'placeables' | 'actors' | 'batch', on: boolean): void
  /** Mark the save dirty so a following flush actually writes. */
  dirtySave(): void
  /** Clear the sim/GL percentile window. Pair with `harness.resetPerf()`. */
  resetSplit(): void
  /** Renderer draw-call accounting reset — call before a timed window. */
  resetInfo(): void
}

declare global {
  interface Window {
    __perf?: PerfApi
  }
}

const EMPTY: LayerCount = {
  meshes: 0,
  visible: 0,
  triangles: 0,
  materials: 0,
  instanced: 0,
  instances: 0,
}

function triCount(geo: THREE.BufferGeometry): number {
  const index = geo.getIndex()
  if (index !== null) return index.count / 3
  const pos = geo.getAttribute('position')
  return pos === undefined ? 0 : pos.count / 3
}

/** Count what one subtree costs. `visible` respects the whole ancestor chain. */
export function countLayer(root: THREE.Object3D | null | undefined): LayerCount {
  if (root === null || root === undefined) return { ...EMPTY }
  const mats = new Set<string>()
  const out: LayerCount = { ...EMPTY }
  root.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (mesh.isMesh !== true) return
    out.meshes++
    let vis = true
    let cur: THREE.Object3D | null = o
    while (cur !== null) {
      if (!cur.visible) {
        vis = false
        break
      }
      cur = cur.parent
    }
    if (vis) out.visible++
    const inst = o as THREE.InstancedMesh
    const n = inst.isInstancedMesh === true ? inst.count : 1
    if (inst.isInstancedMesh === true) {
      out.instanced++
      out.instances += n
    }
    out.triangles += triCount(mesh.geometry) * n
    const m = mesh.material
    if (Array.isArray(m)) for (const mm of m) mats.add(mm.uuid)
    else if (m !== undefined && m !== null) mats.add(m.uuid)
  })
  out.materials = mats.size
  return out
}

function defCosts(): DefCost[] {
  const byDef = new Map<string, DefCost>()
  for (const sceneId of world.ids()) {
    const node = world.require(sceneId)
    for (const p of node.grid.all()) {
      const art = node.art.get(p.instanceId)
      const key = p.defId as string
      let row = byDef.get(key)
      if (row === undefined) {
        const c = countLayer(art)
        let bakeable = 0
        const vetoes = new Map<string, number>()
        const live = liveNamesFor(p.defId)
        art?.traverse((o) => {
          const m = o as THREE.Mesh
          if (m.isMesh !== true) return
          const veto = bakeVeto(m, art, live)
          if (veto === null) bakeable++
          else vetoes.set(veto, (vetoes.get(veto) ?? 0) + 1)
        })
        row = {
          defId: key,
          count: 0,
          bakeableEach: bakeable,
          vetoes: [...vetoes.entries()]
            .sort((x, y) => y[1] - x[1])
            .slice(0, 3)
            .map(([reason, n]) => `${reason}x${n}`),
          meshesEach: c.meshes,
          materialsEach: c.materials,
          trianglesEach: Math.round(c.triangles),
          meshesTotal: 0,
        }
        byDef.set(key, row)
      }
      row.count++
      row.meshesTotal = row.count * row.meshesEach
    }
  }
  return [...byDef.values()].sort((a, b) => b.meshesTotal - a.meshesTotal)
}

/**
 * CPU WORK THAT IS NOT THE GL CALL.
 *
 * `engine.stats.workMs` is every system plus `renderer.render()`. In the
 * headless harness there is no GPU — Chromium falls back to a software
 * rasteriser — so `renderer.render()` there absorbs vertex processing for the
 * whole scene and the number swings with TRIANGLE COUNT in a way no iPad ever
 * will. Measured: hiding the town's placeables took the maxed-town frame from
 * 30 ms to 10 ms while only removing 155 draw calls.
 *
 * So the two halves are timed apart. `simMs` — everything the game itself does,
 * with the GL submit taken out — is real, portable CPU cost and is what the
 * stress gate gates on. `glMs` is reported, never gated, because on this
 * hardware it is not measuring the thing it is named after.
 */
const GL_WINDOW = 240
const glRing = new Float64Array(GL_WINDOW)
const simRing = new Float64Array(GL_WINDOW)
let glHead = 0
let glFilled = 0

/** Forget the window. Must accompany `engine.resetPerf()` or a scene build's
 *  multi-second spike stays in the percentiles for the next 240 frames. */
export function resetSplit(): void {
  glHead = 0
  glFilled = 0
}

function pushSplit(glMs: number): void {
  glRing[glHead] = glMs
  simRing[glHead] = Math.max(0, engine.stats.workMs - glMs)
  glHead = (glHead + 1) % GL_WINDOW
  if (glFilled < GL_WINDOW) glFilled++
}

function pct(ring: Float64Array, q: number): number {
  if (glFilled === 0) return 0
  const a = Array.from(ring.subarray(0, glFilled)).sort((x, y) => x - y)
  return a[Math.min(a.length - 1, Math.floor(q * a.length))] ?? 0
}

export function installPerf(ctx: GameContext): void {
  installGovernor(ctx)

  // Time the GL submit separately from everything else. Wrapping our own
  // renderer instance keeps `src/render/renderer.ts` and the frozen engine
  // contract untouched.
  const originalRender = ctx.renderer.render.bind(ctx.renderer)
  ctx.renderer.render = (camera, frameMs): void => {
    const t = performance.now()
    originalRender(camera, frameMs)
    pushSplit(performance.now() - t)
  }

  installBatcher()
  engine.addSystem({
    id: 'perf.batcher',
    // Band 20-29 is the build system: rebaking has to happen after a placement
    // has landed and before the frame that draws it.
    order: 29,
    render: () => batcherTick(),
  })

  const api: PerfApi = {
    report: () => {
      const scenes: SceneReport[] = world.ids().map((id) => {
        const n = world.require(id)
        return {
          id: id as string,
          awake: n.awake,
          attached: n.root.parent !== null,
          placements: n.grid.count(),
          ground: countLayer(n.ground),
          statics: countLayer(n.statics),
          placeables: countLayer(n.placeables),
          actors: countLayer(n.actors),
        }
      })
      const whole = countLayer(ctx.renderer.scene)
      return {
        drawCalls: engine.stats.drawCalls,
        triangles: engine.stats.triangles,
        programs: engine.stats.programs,
        renderScale: engine.stats.renderScale,
        pixelRatio: engine.stats.pixelRatio,
        workMs: engine.stats.workMs,
        simP50: pct(simRing, 0.5),
        simP95: pct(simRing, 0.95),
        glP50: pct(glRing, 0.5),
        glP95: pct(glRing, 0.95),
        sceneMeshes: whole.meshes,
        visibleMeshes: whole.visible,
        scenes,
        defs: defCosts(),
        governor: governorReport(),
        batcher: batcherReport(),
      }
    },
    stress: (opts) => buildStress(opts ?? {}),
    setGovernor: (on) => {
      ctx.renderer.setAdaptive(on)
    },
    governor: () => governorReport(),
    setBatching: (on) => setBatchingEnabled(on),
    batcher: () => batcherReport(),
    instanceState: (id) => instanceState(id as never),
    profile: (ms) => profileSystems(ms ?? 4000),
    freeze: (on) => {
      for (const id of engine.systemIds()) {
        // Everything except the frame itself and this module's own upkeep.
        if (id === 'render.frame' || id.startsWith('perf.')) continue
        engine.setPaused(id, on)
      }
      /*
       * AND THE CSS. Pausing engine systems does not stop `mt-sign-bob`, the
       * 2.8 s keyframe loop on every signpost — which is a DOM animation the
       * engine has no say over. Two captures taken seconds apart therefore
       * caught the signs at different phases, and that alone was ~1% of the
       * frame: enough to swamp an A/B of the renderer and make the visual gate
       * useless. Freezing the world has to mean the HUD too.
       */
      const ID = 'mt-perf-freeze'
      const existing = document.getElementById(ID)
      if (on) {
        if (existing !== null) return
        const style = document.createElement('style')
        style.id = ID
        style.textContent =
          '*, *::before, *::after { animation-play-state: paused !important;' +
          ' transition: none !important; }'
        document.head.appendChild(style)
      } else existing?.remove()
    },
    setLayerVisible: (layer, on) => {
      const id = world.activeSceneId
      if (id === null) return
      const n = world.require(id)
      if (layer === 'batch') {
        const g = n.placeables.children.find((c) => c.name.endsWith(':batch'))
        if (g !== undefined) g.visible = on
        return
      }
      n[layer].visible = on
    },
    frameBreakdown: (samples = 40) => {
      const r = ctx.renderer.renderer
      const scene = ctx.renderer.scene
      const cam = ctx.camera.camera
      const objects = { total: 0, groups: 0, meshes: 0 }
      scene.traverse((o) => {
        objects.total++
        if ((o as THREE.Mesh).isMesh === true) objects.meshes++
        else objects.groups++
      })
      const time = (n: number, fn: () => void): number => {
        const t = performance.now()
        for (let i = 0; i < n; i++) fn()
        return (performance.now() - t) / n
      }
      const matrixMs = time(samples, () => scene.updateMatrixWorld(true))
      const wasShadow = r.shadowMap.enabled
      r.shadowMap.enabled = false
      const noShadowMs = time(samples, () => r.render(scene, cam))
      r.shadowMap.enabled = wasShadow
      const fullMs = time(samples, () => r.render(scene, cam))
      return {
        objects: objects.total,
        groups: objects.groups,
        meshes: objects.meshes,
        matrixWorldMs: +matrixMs.toFixed(2),
        renderNoShadowMs: +noShadowMs.toFixed(2),
        renderFullMs: +fullMs.toFixed(2),
        shadowMs: +(fullMs - noShadowMs).toFixed(2),
        draws: r.info.render.calls,
      }
    },
    dirtySave: () => save.request('perf:measure'),
    resetSplit: () => resetSplit(),
    resetInfo: () => {
      ctx.renderer.renderer.info.reset()
    },
  }

  window.__perf = api
}
